// @flow
import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { filter, isEmpty } from 'lodash';
import { NavigationUtil } from 'utils/NavigationUtil';
import { RouteNames } from 'router';
import { HomeActions } from 'modules/home/actions';
import { HomeSelector } from 'modules/home/selector';
import { PageLoader } from 'components/molecules/pageLoader';
import MusicFoundationBanner from 'components/organisms/musicFoundationBanner';
import { ContactUsSection } from 'components/templates/contactUsSection';
import FooterSection from 'components/templates/footer';
import { ProjectSelector } from 'modules/project/selector';
import PageTemplate from 'components/templates/pageTemplate';
import { ProjectActions } from 'modules/project/actions';
import { useViewport } from 'components/templates/viewportProvider';
import type { AppState } from 'modules/rootReducer';
import type { Contact, SiteSettings, Vertical } from 'types/common';
import UpcomingShowsSection from 'components/templates/upcomingShowsSection';
import PastEventsSection from 'components/templates/pastEvents';
import { EventSelector } from 'modules/event/selector';
import { EventActions } from 'modules/event/actions';
import { ProjectAnnouncementsSection } from 'components/templates/projectAnnouncementsSection';
import VisionPhilosophySection from 'components/templates/vision&PhilosophySection';
import JoinOurTeamAbout from 'components/templates/joinOurTeamAbout';
import { CmsPagesActions } from 'modules/cmsPages/actions';
import { CmsPagesSelector } from 'modules/cmsPages/selector';
import Team from 'components/templates/team';
import { TeamSelector } from 'modules/team/selector';
import { TeamActions } from 'modules/team/action';
import { CmsDetailsActions } from 'modules/cmsDetails/actions';
import { CmsDetailsSelector } from 'modules/cmsDetails/selector';
import MusicFoundation from 'components/templates/musicFoundation';
import { pastEventsIntro } from 'Mock/pastEvents';
import { CareerSelector } from 'modules/careers/selector';
import { CareerActions } from 'modules/careers/actions';
import NewsSection from 'components/templates/newsSection';
import { AboutActions } from 'modules/about/actions';
import { AboutPageSelector } from 'modules/about/selector';
import RenderSEO from 'components/atoms/renderSEO';
import ImageGallerySection from 'components/templates/imageGallerySection';
import { FooterSelector } from 'modules/footerLink/selector';
import { FooterActions } from 'modules/footerLink/actions';
import PastEventsBannerTemplate from 'components/templates/pastEventsBanner';

type Props = {
  isLoading: boolean,
  verticals: Array<Vertical>,
  verticalId: number | null,
  getVerticals: () => void,
  siteSettings: SiteSettings,
  getSiteSettings: () => void,
  contact: Contact,
  getContactDetails: () => void,
};

const EventsPage = (props: Props) => {
  const {
    isLoading,
    getVerticals,
    verticals,
    verticalId,
    getPageData,
    visionPhilosophySection,
    foundationData,
    getGalleryForEvent,
    gallery,
    getUpcomingEvents,
    upcomingShows,
    getSiteSettings,
    footerLink,
    getFooterLinkData,
    siteSettings,
    contact,
    getContactDetails,
    getBannerData,
    musicBanner,
    getTeamData,
    teamData,
    getAboutNews,
    newsData,
    cmsDetailsData,
    getAnnouncements,
    announcements,
    getCmsDetailsData,
    getJobPosts,
    jobPosts,
    upcomingShowsOverlayData,
  } = props;

  const { isMobile } = useViewport();
  let [theme, setTheme] = useState(isMobile ? 'dark-theme' : 'light-theme');

  useEffect(() => {
    getBannerData();
    getContactDetails();
    getVerticals();
    getPageData();
    getSiteSettings();
    getUpcomingEvents();
    getTeamData(verticalId);
    getAboutNews();
    getJobPosts(verticalId);
    getAnnouncements(verticalId);
    getCmsDetailsData();
    getFooterLinkData();
    getGalleryForEvent(verticalId);
  }, []);

  if (isLoading) {
    return <PageLoader isMobile={isMobile} />;
  }

  if (
    !contact ||
    !siteSettings ||
    !verticals ||
    !gallery ||
    !upcomingShows ||
    !visionPhilosophySection ||
    !teamData ||
    !foundationData ||
    !jobPosts ||
    !musicBanner ||
    !newsData ||
    !cmsDetailsData ||
    !upcomingShowsOverlayData ||
    !footerLink
  ) {
    return null;
  }

  const selectedVertical = filter(verticals, ({ id }) => id === verticalId);
  const metaTagSEO =
    selectedVertical.length > 0 ? selectedVertical[0].metaTagSEO : '';
  const componentList = [
    ...(!isEmpty(musicBanner)
      ? [
          {
            component: MusicFoundationBanner,
            data: musicBanner,
            theme: isMobile ? 'dark-theme' : 'light-theme',
            sectionId: 'banner',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: RouteNames.musicEvents,
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(upcomingShows)
      ? [
          {
            component: UpcomingShowsSection,
            data: {
              upcomingShows,
              upcomingShowsOverlayData,
              showNotificationButton: true,
            },
            theme: 'dark-theme',
            sectionId: 'upcoming-shows',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: '#',
              },
              {
                breadcrumbName: 'Upcoming Shows',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
      ...(!isEmpty(announcements)
      ? [
          {
            component: ProjectAnnouncementsSection,
            data: { announcements },
            sectionId: 'announcements',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: '#',
              },
              {
                breadcrumbName: 'Announcements',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
            sectionName: 'announcement-section',
          },
        ]
      : []),
          {
            component: PastEventsBannerTemplate,
            theme: 'light-theme',
            sectionId: 'pastEventsBanner',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: '#',
              },
              {
                breadcrumbName: 'Past Events',
                breadcrumbLink: '#',
              },
            ],
          },
    ...(!isEmpty(visionPhilosophySection.visionSection)
      ? [
          {
            component: VisionPhilosophySection,
            data: {
              visionPhilosophySection,
              showButton: false,
              showCount: true,
            },
            mobileBannerData: visionPhilosophySection.visionSectionMobile,
            theme: 'light-theme',
            sectionId: 'vision-and-philosophy',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: '#',
              },
              {
                breadcrumbName: 'Vision & Philosophy',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(newsData.result_news_data)
      ? [
          {
            component: NewsSection,
            data: {
              newsData,
              sectionHeading: 'Featured Publications',
              overlayButtonTextDesktop: 'SEE ALL',
              overlayButtonTextMobile: 'VIEW ALL',
              showNotificationButton: true,
              toggleId: true,
            },
            sectionId: 'publications',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: '#',
              },
              {
                breadcrumbName: 'Publications',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
          },
        ]
      : []),
    ...(!isEmpty(gallery)
      ? [
          {
            component: ImageGallerySection,
            data: {
              images: gallery,
            },
            theme: 'dark-theme',
            sectionId: 'gallery',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: '#',
              },
              {
                breadcrumbName: `Gallery`,
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(foundationData) && !isMobile
      ? [
          {
            component: MusicFoundation,
            data: {
              foundationData,
              cmsDetailsData,
              show_desktop_overlay_button: true,
            },
            theme: 'light-theme',
            sectionId: 'foundation',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: '#',
              },
              {
                breadcrumbName: 'Music Foundation',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(teamData)
      ? [
          {
            component: Team,
            sectionId: 'team',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: RouteNames.about,
              },
              {
                breadcrumbName: 'Music Foundation Team',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
            data: {
              type: 'About',
              teamData,
              cmsDetailsData,
              show_desktop_overlay_button: true,
            },
          },
        ]
      : []),
    ...(!isEmpty(jobPosts)
      ? [
          {
            component: JoinOurTeamAbout,
            data: { jobPosts, toggleId: true },
            sectionId: 'careers',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: RouteNames.musicEvents,
              },
              {
                breadcrumbName: 'Careers',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
          },
        ]
      : []),
    {
      component: ContactUsSection,
      data: { contact, verticals, verticalId },
      location: props,
      sectionId: 'contact',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Music & Events',
          breadcrumbLink: RouteNames.musicEvents,
        },
        {
          breadcrumbName: 'Contact Us',
          breadcrumbLink: '#',
        },
      ],
      theme: 'light-theme',
    },
    {
      component: FooterSection,
      theme: 'dark-theme',
      sectionId: 'te-links',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Music & Events',
          breadcrumbLink: RouteNames.musicEvents,
        },
        {
          breadcrumbName: 'Links',
          breadcrumbLink: '#',
        },
      ],
      data: { siteSettings, footerLink },
    },
  ];

  return (
    <>
      <RenderSEO metaTags={metaTagSEO} currentRouteName={'music-and-events'} />
      <PageTemplate
        theme={theme}
        componentList={componentList}
        updateTheme={e => setTheme(e)}
        verticals={verticals}
        siteSettings={siteSettings}
      />
    </>
  );
};

const mapStateToProps = (state: AppState, props: any) => {
  const verticalKey = NavigationUtil.getVerticalKeyForLocation(props.location);

  return {
    isLoading:
      HomeSelector.isLoading(state) ||
      FooterSelector.isLoading(state) ||
      CmsPagesSelector.isCmsPageLoading(state) ||
      CmsDetailsSelector.getCmsDetailsDataLoading(state) ||
      EventSelector.getUpcomingEventsLoading(state) ||
      AboutPageSelector.getAboutNewsLoading(state),
    announcements: ProjectSelector.getAnnouncements(state),
    gallery: EventSelector.getGallery(state),
    contact: HomeSelector.getContactForVertical(state, verticalKey),
    verticalId: HomeSelector.getVerticalIdForKey(verticalKey),
    verticals: HomeSelector.getVerticals(state),
    teamData: TeamSelector.getTeamData(state),
    footerLink: FooterSelector.getFooterData(state),
    visionPhilosophySection: CmsPagesSelector.getVisionPhilosophy(
      state,
      '',
      'music',
      true
    ),
    foundationData: CmsPagesSelector.getMusicFoundation(
      state,
      'Music Foundation',
      'music & events',
      true
    ),
    newsData: AboutPageSelector.getAboutNews(state, 'Publications', 'music'),
    siteSettings: HomeSelector.getSiteSettings(state),
    upcomingShows: EventSelector.getUpcomingEvents(state),
    cmsDetailsData: CmsDetailsSelector.getCmsDetailsData(state),
    musicBanner: HomeSelector.getBanners(state, true, 'music', ''),
    jobPosts: CareerSelector.getJobPosts(state),
    upcomingShowsOverlayData: EventSelector.getUpcomingEventsOverlayData(state),
  };
};
const mapDispatchToProps = dispatch => {
  const {
    getBannerData,
    getContactDetails,
    getVerticals,
    getSiteSettings,
  } = HomeActions;
  const { getPageData } = CmsPagesActions;
  const {
    getUpcomingEvents,
    getGalleryForEvent,
  } = EventActions;
  const { getAnnouncements } = ProjectActions;
  const { getTeamData } = TeamActions;
  const { getCmsDetailsData } = CmsDetailsActions;
  const { getJobPosts } = CareerActions;
  const { getFooterLinkData } = FooterActions;
  const { getAboutNews } = AboutActions;
  return bindActionCreators(
    {
      getBannerData,
      getGalleryForEvent,
      getContactDetails,
      getVerticals,
      getPageData,
      getAnnouncements,
      getSiteSettings,
      getUpcomingEvents,
      getTeamData,
      getCmsDetailsData,
      getJobPosts,
      getAboutNews,
      getFooterLinkData,
    },
    dispatch
  );
};
export default connect(mapStateToProps, mapDispatchToProps)(EventsPage);
